import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowLeft, MapPin, Star, Quote, ArrowRight } from "lucide-react";
import { api } from "../../lib/api";
import { EmptyState } from "../../components/shared/EmptyState";
import { ReassurancePanel } from "./BlogArticlePage";

// Storytelling immersif : le récit est tissé AU MILIEU des photos, jamais isolé.
// Bloc 1 : introduction + grande photo d'ambiance.
// Bloc 2 : sections alternées — texte d'un côté, diptyque de photos de l'autre.
// Bloc 3 : encart « L'avis des mariés » sur fond doux.
// Bloc 4 : conclusion + prestataires + produits loués.
export default function RealisationDetailPage() {
  const { slug } = useParams();
  const [r, setR] = useState(undefined);
  useEffect(() => {
    api.get(`/gallery/${slug}`).then((res) => setR(res.data)).catch(() => setR(null));
  }, [slug]);

  if (r === undefined) return <div className="py-16 text-center font-heading text-deepgreen" data-testid="realisation-loading">Chargement…</div>;
  if (r === null) {
    return (
      <div className="container-ae py-12 md:py-16" data-testid="realisation-detail-page">
        <Link to="/realisations" className="inline-flex items-center gap-2 text-sm font-semibold text-deepgreen hover:text-sage" data-testid="realisation-back">
          <ArrowLeft className="h-4 w-4" /> Toutes les réalisations
        </Link>
        <div className="mt-8"><EmptyState title="Réalisation introuvable">Cette réalisation n'est plus publiée.</EmptyState></div>
      </div>
    );
  }

  const gallery = r.images?.length ? r.images : [r.cover_image].filter(Boolean);
  const sections = r.sections || [];
  // Sans sections structurées, le récit long (description) devient le bloc de texte à tisser
  const story = sections.length > 0 ? sections : (r.description ? [{ title: null, text: r.description }] : []);
  const conclusion = sections.length > 0 ? r.description : null;

  // Distribution des photos au fil du récit : la première en grande ambiance,
  // puis 2 par section (diptyque), le reste en diptyques avant l'avis.
  const ambience = gallery[0];
  let cursor = 1;
  const storyBlocks = story.map((s) => {
    const imgs = gallery.slice(cursor, cursor + 2);
    cursor += imgs.length;
    return { ...s, imgs };
  });
  const remaining = gallery.slice(cursor);
  const remainingRows = [];
  for (let i = 0; i < remaining.length; i += 2) remainingRows.push(remaining.slice(i, i + 2));

  return (
    <div data-testid="realisation-detail-page">
      {/* Hero pleine largeur */}
      <div className="relative overflow-hidden bg-deepgreen">
        {gallery[0] && <img src={gallery[0]} alt={r.title} className="absolute inset-0 h-full w-full object-cover opacity-45" />}
        <div className="absolute inset-0 bg-gradient-to-t from-deepgreen via-deepgreen/40 to-transparent" />
        <div className="container-ae relative pb-14 pt-16 md:pb-20 md:pt-24">
          <Link to="/realisations" className="inline-flex items-center gap-2 text-sm font-semibold text-cream/80 hover:text-cream" data-testid="realisation-back">
            <ArrowLeft className="h-4 w-4" /> Toutes les réalisations
          </Link>
          <div className="mt-6 flex flex-wrap gap-2" data-testid="realisation-badges">
            {(r.tags || []).map((t) => (
              <span key={t} className="rounded-full border border-cream/30 bg-cream/10 px-3.5 py-1 text-[10px] font-bold uppercase tracking-wider text-cream backdrop-blur-sm">{t}</span>
            ))}
          </div>
          <h1 className="mt-4 max-w-3xl text-3xl text-cream sm:text-4xl lg:text-5xl" data-testid="realisation-title">{r.title}</h1>
          <p className="mt-3 flex items-center gap-2 text-sm text-cream/80">
            <MapPin className="h-4 w-4 text-gold" /> {r.place}{r.date ? ` — ${r.date}` : ""}
          </p>
        </div>
      </div>

      <div className="container-ae py-14 md:py-20">
        {/* Bloc 1 — Introduction + grande photo d'ambiance */}
        {r.excerpt && (
          <p className="mx-auto max-w-2xl text-center font-heading text-xl italic leading-relaxed text-deepgreen/80" data-testid="realisation-excerpt">
            {r.excerpt}
          </p>
        )}
        {ambience && (
          <img src={ambience} alt={`${r.title} — ambiance`} className="mt-12 aspect-[16/9] w-full rounded-2xl object-cover shadow-lg" data-testid="story-ambience" />
        )}

        {/* Bloc 2 — Récit tissé au milieu des photos : texte / diptyque en alternance */}
        {storyBlocks.length > 0 && (
          <div className="mt-16 space-y-16 md:mt-20 md:space-y-24" data-testid="realisation-sections">
            {storyBlocks.map((s, i) => {
              const textLeft = i % 2 === 0;
              const noPhoto = s.imgs.length === 0;
              return (
                <div key={i} className={noPhoto ? "mx-auto max-w-2xl text-center" : "grid items-center gap-8 lg:grid-cols-2 lg:gap-14"} data-testid={`realisation-section-${i}`}>
                  <div className={noPhoto || textLeft ? "" : "lg:order-2"}>
                    <p className="overline-ae">{String(i + 1).padStart(2, "0")}</p>
                    {s.title && <h2 className="mt-3 font-heading text-2xl text-deepgreen sm:text-3xl">{s.title}</h2>}
                    {s.text && <div className="prose-ae mt-4 text-base leading-relaxed text-foreground/80" dangerouslySetInnerHTML={{ __html: s.text }} />}
                  </div>
                  {!noPhoto && (
                    <div className={`${textLeft ? "" : "lg:order-1"} ${s.imgs.length === 2 ? "grid grid-cols-2 gap-4" : ""}`}>
                      {s.imgs.map((img, j) => (
                        <img key={j} src={img} alt={`${s.title || r.title} — photo ${j + 1}`} loading="lazy"
                          className={`w-full rounded-xl object-cover shadow-lg ${s.imgs.length === 2 ? "aspect-[3/4]" : "aspect-[4/3]"}`}
                          data-testid={`section-image-${i}-${j}`} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Photos restantes — diptyques de détails */}
        {remainingRows.length > 0 && (
          <div className="mt-16 space-y-5" data-testid="realisation-gallery">
            {remainingRows.map((row, ri) => (
              <div key={ri} className={`grid gap-5 ${row.length === 2 ? "md:grid-cols-2" : ""}`} data-testid={`gallery-row-${ri}`}>
                {row.map((img, i) => (
                  <img key={i} src={img} alt={`${r.title} — détail`} loading="lazy"
                    className={`w-full rounded-xl object-cover transition-transform duration-500 hover:scale-[1.02] ${row.length === 2 ? "aspect-[4/3]" : "aspect-[21/9]"}`} />
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Bloc 3 — L'avis des mariés : rupture de rythme sur fond doux */}
        {r.review_text && (
          <figure className="relative mx-auto mt-20 max-w-3xl overflow-hidden rounded-2xl bg-sage-light px-8 py-14 text-center md:px-16" data-testid="realisation-review">
            <Quote className="absolute -left-4 -top-4 h-28 w-28 text-sage/20" aria-hidden />
            <Quote className="absolute -bottom-4 -right-4 h-28 w-28 rotate-180 text-sage/20" aria-hidden />
            <p className="overline-ae">L'avis des mariés</p>
            <blockquote className="relative mt-5 font-heading text-2xl italic leading-relaxed text-deepgreen md:text-3xl">
              « {r.review_text} »
            </blockquote>
            {r.review_rating && (
              <div className="mt-6 flex justify-center gap-1.5" data-testid="review-stars">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className={`h-6 w-6 ${i < r.review_rating ? "fill-gold text-gold" : "text-deepgreen/20"}`} />
                ))}
              </div>
            )}
            <figcaption className="mt-4 text-sm font-semibold uppercase tracking-[0.15em] text-deepgreen">
              {r.review_author}
              {r.review_source && <span className="block pt-1 text-[11px] font-normal normal-case tracking-normal text-muted-foreground">Avis vérifié — {r.review_source}</span>}
            </figcaption>
          </figure>
        )}

        {/* Bloc 4 — Conclusion + prestataires + produits loués */}
        {conclusion && (
          <div className="mx-auto mt-20 max-w-2xl text-center" data-testid="realisation-description">
            <p className="overline-ae">Pour finir</p>
            <div className="prose-ae mt-4 text-base leading-relaxed text-foreground/80" dangerouslySetInnerHTML={{ __html: conclusion }} />
          </div>
        )}

        {(r.vendors || []).length > 0 && (
          <div className="mt-16 text-center" data-testid="realisation-vendors">
            <p className="overline-ae">Les prestataires de ce jour</p>
            <div className="mt-6 flex flex-wrap justify-center gap-x-12 gap-y-8">
              {r.vendors.map((v, i) => {
                const inner = (
                  <>
                    <p className="text-base font-bold uppercase tracking-[0.15em] text-deepgreen sm:text-lg">{v.role}</p>
                    <p className="mt-1 font-heading text-lg text-foreground/75">{v.name}</p>
                  </>
                );
                return v.link ? (
                  <a key={i} href={v.link} target="_blank" rel="noreferrer" className="transition-colors hover:text-sage" data-testid={`vendor-${i}`}>{inner}</a>
                ) : (
                  <div key={i} data-testid={`vendor-${i}`}>{inner}</div>
                );
              })}
            </div>
          </div>
        )}

        {(r.products || []).length > 0 && (
          <div className="mt-20" data-testid="related-products">
            <div className="text-center">
              <p className="overline-ae">Recréez cette ambiance</p>
              <h2 className="mt-3 font-heading text-2xl text-deepgreen sm:text-3xl">Louer la décoration de ce mariage</h2>
            </div>
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {r.products.map((p) => (
                <Link key={p.slug} to={`/produit/${p.slug}`} className="group card-ae overflow-hidden transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl" data-testid={`related-product-${p.slug}`}>
                  {p.image && (
                    <div className="overflow-hidden">
                      <img src={p.image} alt={p.name} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-3 p-5">
                    <div>
                      {p.category && <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">{p.category}</p>}
                      <p className="mt-1 font-semibold text-deepgreen">{p.name}</p>
                      {p.price_ttc != null && <p className="mt-1 text-sm text-muted-foreground">{p.price_ttc.toFixed(2)} € TTC / weekend</p>}
                    </div>
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-deepgreen text-cream transition-colors group-hover:bg-gold group-hover:text-deepgreen">
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <ReassurancePanel />
      </div>
    </div>
  );
}
